import React from 'react'
import { Check, X } from 'lucide-react'
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation'

const PasswordStrengthIndicator = ({ password, className = "" }) => {
  const { requirements, errors } = validatePassword(password)
  const { strength, score } = getPasswordStrength(password)

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'excellent': return 'bg-green-500'
      case 'strong': return 'bg-blue-500'
      case 'medium': return 'bg-yellow-500'
      case 'weak': return 'bg-red-500'
      default: return 'bg-gray-300'
    }
  }

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'excellent': return 'Excellent'
      case 'strong': return 'Strong'
      case 'medium': return 'Medium'
      case 'weak': return 'Weak'
      default: return 'Very Weak'
    }
  }

  if (!password) return null

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-secondary-700">Password Strength</div>
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${
          strength === 'excellent' ? 'text-green-700 bg-green-100' :
          strength === 'strong' ? 'text-blue-700 bg-blue-100' :
          strength === 'medium' ? 'text-yellow-700 bg-yellow-100' :
          'text-red-700 bg-red-100'
        }`}>
          {getStrengthText(strength)}
        </span>
      </div>
      
      <div className="flex space-x-2">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-3 flex-1 rounded-full transition-all duration-300 ${
              level <= score ? getStrengthColor(strength) : 'bg-secondary-200'
            } ${level <= score ? 'animate-pulse' : ''}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2">
        {[
          { key: 'length', text: 'At least 12 characters' },
          { key: 'lowercase', text: 'One lowercase letter' },
          { key: 'uppercase', text: 'One uppercase letter' },
          { key: 'digit', text: 'One digit' },
          { key: 'symbol', text: 'One symbol (!@#$%^&*...)' }
        ].map(({ key, text }) => (
          <div key={key} className={`flex items-center space-x-3 p-2 rounded-lg transition-all duration-200 ${
            requirements[key] ? 'bg-green-50 border border-green-200' : 'bg-secondary-50 border border-secondary-200'
          }`}>
            <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-200 ${
              requirements[key] ? 'bg-green-500' : 'bg-secondary-300'
            }`}>
              {requirements[key] ? (
                <Check className="h-3 w-3 text-white" />
              ) : (
                <X className="h-3 w-3 text-white" />
              )}
            </div>
            <span className={`text-sm font-medium transition-colors duration-200 ${
              requirements[key] ? 'text-green-700' : 'text-secondary-600'
            }`}>
              {text}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PasswordStrengthIndicator
